import React, {useRef, useState} from "react";
import {Button, Col, Drawer, Form, Input, Row, Select} from "antd";
import PropTypes from "prop-types";

import {createFob} from "../../helpers/fob";
import confirmAction from "../../helpers/confirmAction";
import {messageType, showMessages} from "../../helpers/message";
import {accessKeySuccessfullyAssignedText} from "../../helpers/text";

import "./AccessKey.less"

const {Option} = Select;

const AccessKeyAddForm = ({drawerVisible, hideDrawer, refreshFobs, fobs}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [loading, setLoading] = useState(false);

    const [form] = Form.useForm();
    const mobileMode = window.innerWidth <= 768;

    const closeDrawer = () => {
        form.resetFields();
        hideDrawer();
    }

    const handleOk = async ([identifier, type, abortSignal, forceAssign]) => {
        await createFob({identifier, type, forceAssign}, abortSignal);
        onFinish(accessKeySuccessfullyAssignedText, messageType.success);
    }

    const handleCancel = () => {
        onFinish('Nothing to update.', messageType.info);
    }

    const onFinish = (message, messageType) => {
        refreshFobs();
        setLoading(false);
        closeDrawer(true);
        showMessages(message, messageType);
    }

    const handleFinish = async (values) => {
        const abortSignal = abortSignalRef.current;
        const identifier = values['Key ID'];
        const type = values['Type'];
        const forceAssign = true;
        const confirmActionParams = [identifier, type, abortSignal, forceAssign];

        setLoading(true);

        const alreadyHoldsThisKey = fobs.some(fob => fob.identifier === identifier && !fob.ended_at);
        // If user has fobs (passed down via props), check if this new fob id matches any and has not ended yet.
        if (alreadyHoldsThisKey) {
            // Current user is already assigned this key.
            return onFinish(`Access Key ${identifier} is already assigned to this member.`, messageType.info);
        }

        try {
            // If the fob is not assigned, no need for confirmAction nor force_assign flag.
            // If there is a conflict, it will go to the catch block, and prompt staff with the confirmAction.
            await createFob({identifier, type}, abortSignal);
            onFinish(accessKeySuccessfullyAssignedText, messageType.success);

        } catch (error) {

            // Status 409 refers to a conflict with the request and the server
            if (error.status_code === 409 ||
                error.message === 'This access key is currently assigned to a member. Please try again.') {
                return confirmAction(
                            'Are you sure you want to assign this access key to this member?',
                            `This key is currently assigned to another member.
                            Clicking "Yes" will unassign it from the previous member and assign it to this one.`,
                            await handleOk,
                            confirmActionParams,
                            handleCancel,
                            'Yes',
                            'Cancel'
                );
            }

            onFinish(error.message, messageType.error);
        }
    }

    return (
        <Drawer className="assign-access-key-drawer"
                width={mobileMode ? '100%' : '45%'}
                title="Assign Access Key"
                placement="right"
                closable={true}
                onClose={closeDrawer}
                open={drawerVisible}
        >
            <Form
                form={form}
                layout="vertical"
                name="assign-access-key"
                initialValues={{
                    'Type': 'awid',
                }}
                onFinish={handleFinish}
            >
                <Row className="full-width-row">
                    <Col>
                        <Form.Item
                            name="Type"
                            label="Type: "
                            rules={[{type: 'string', required: true}]}
                        >
                        <Select>
                                <Option value="awid">AWID</Option>
                        </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row className="full-width-row">
                    <Col>
                        <Form.Item
                            name="Key ID"
                            label="Key ID: "
                            rules={[{type: 'string', required: true}]}
                            >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Button className="assign-access-key-cancel-btn" onClick={closeDrawer}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={loading}>Assign</Button>
                </Row>
            </Form>
        </Drawer>
    )
}

export default AccessKeyAddForm;

AccessKeyAddForm.propTypes = {
    fobs: PropTypes.array.isRequired,
    drawerVisible: PropTypes.bool.isRequired,
    hideDrawer: PropTypes.func.isRequired,
    refreshFobs: PropTypes.func.isRequired,
};
